<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    fullscreen
    aria-labelledby="guided-recording-invitations-dialog-title"
    data-cy="guidedRecording-invitations-dialog"
  >
    <template v-slot:activator="{ props }">
      <slot v-bind="{ props }" />
    </template>

    <v-card data-cy="guidedRecording-invitations-card">
      <v-card-title class="d-flex justify-space-between" data-cy="guidedRecording-invitations-title">
        <h1
          id="guided-recording-invitations-dialog-title"
          class="guided-recording-invitations-dialog__title"
          tabindex="-1"
        >
          {{ $t('routes.guidedRecording.invitationsTitle') }}
        </h1>
        <v-btn
          icon="mdi-close"
          variant="text"
          :aria-label="$t('common.close')"
          data-cy="guidedRecording-invitations-close"
          @click="dialog = false"
        />
      </v-card-title>

      <v-card-text class="guided-recording-invitations-dialog__content mt-4">
        <div class="d-flex justify-end mb-4">
          <v-btn
            color="primary"
            prepend-icon="mdi-video-plus-outline"
            data-cy="guidedRecording-invitations-create"
            @click="$emit('create')"
          >
            {{ $t('routes.guidedRecording.newInvitation') }}
          </v-btn>
        </div>

        <v-progress-linear
          v-if="isLoading"
          indeterminate
          color="primary"
          class="mb-4"
          :aria-label="$t('routes.guidedRecording.loadingInvitations')"
          data-cy="guidedRecording-invitations-loading"
        />

        <v-alert
          v-if="listError"
          ref="listError"
          type="error"
          variant="tonal"
          role="alert"
          tabindex="-1"
          data-cy="guidedRecording-invitations-error"
        >
          <div class="d-flex flex-wrap align-center justify-space-between ga-3">
            <span>{{ listError }}</span>
            <v-btn
              variant="outlined"
              @click="loadInvitations"
            >
              {{ $t('routes.guidedRecording.retryInvitations') }}
            </v-btn>
          </div>
        </v-alert>

        <section
          v-else-if="!isLoading && invitations.length === 0"
          class="text-center py-12"
          aria-labelledby="guided-recording-invitations-empty-heading"
          data-cy="guidedRecording-invitations-empty"
        >
          <v-icon size="48" color="secondary" aria-hidden="true">mdi-clipboard-text-outline</v-icon>
          <h2 id="guided-recording-invitations-empty-heading" class="text-h6 mt-3">
            {{ $t('routes.guidedRecording.invitationsEmptyTitle') }}
          </h2>
          <p class="text-body-2 text-medium-emphasis mb-0">
            {{ $t('routes.guidedRecording.invitationsEmptyDescription') }}
          </p>
        </section>

        <v-data-iterator
          v-else-if="!isLoading"
          :items="invitations"
          :items-per-page="itemsPerPage"
          style="width: 100%;"
          data-cy="guidedRecording-invitations-list"
        >
          <template v-slot:default="{ items }">
            <v-expansion-panels
              v-model="selectedInvitationId"
              variant="accordion"
              class="guided-recording-invitations-dialog__panels"
              @update:model-value="loadInvitation"
            >
              <v-expansion-panel
                v-for="{ raw: invitation } in items"
                :key="invitation.id"
                :value="invitation.id"
                :data-cy="`guidedRecording-invitations-item-${invitation.id}`"
              >
                <v-expansion-panel-title class="guided-recording-invitations-dialog__panel-title py-4">
                  <div class="guided-recording-invitations-dialog__panel-grid">
                    <div class="guided-recording-invitations-dialog__panel-primary">
                      <div class="d-flex flex-wrap align-center ga-2">
                        <span class="text-subtitle-1 font-weight-bold text-break">
                          {{ invitation.title }}
                        </span>
                        <v-chip
                          v-for="status in statuses(invitation)"
                          :key="status"
                          size="small"
                          :color="status === 'active' ? 'success' : 'secondary'"
                          variant="tonal"
                        >
                          {{ $t(`routes.guidedRecording.${status}`) }}
                        </v-chip>
                      </div>
                    </div>
                    <div class="guided-recording-invitations-dialog__panel-field">
                      <div class="text-body-2 text-medium-emphasis">
                        {{ $t('routes.guidedRecording.projectFolder') }}
                      </div>
                      <div class="text-body-2 text-break">
                        {{ nodeName(invitation.projectnode) }}
                      </div>
                    </div>
                    <div class="guided-recording-invitations-dialog__panel-field">
                      <div class="text-body-2 text-medium-emphasis">
                        {{ $t('routes.guidedRecording.destination') }}
                      </div>
                      <div class="text-body-2 text-break">
                        <router-link
                          v-if="nodeId(invitation.destinationnode)"
                          :to="`/node/${nodeId(invitation.destinationnode)}`"
                          :data-cy="`guidedRecording-invitations-destination-${invitation.id}`"
                          @click.stop="dialog = false"
                        >
                          {{ nodeName(invitation.destinationnode) }}
                        </router-link>
                        <span v-else>{{ nodeName(invitation.destinationnode) }}</span>
                      </div>
                    </div>
                  </div>
                </v-expansion-panel-title>

                <v-expansion-panel-text>
                  <v-progress-linear
                    v-if="isDetailLoading(invitation.id)"
                    indeterminate
                    color="primary"
                    class="mb-4"
                    :aria-label="$t('routes.guidedRecording.loadingInvitationDetails')"
                    :data-cy="`guidedRecording-invitations-detail-loading-${invitation.id}`"
                  />

                  <v-alert
                    v-else-if="detailError"
                    type="error"
                    variant="tonal"
                    role="alert"
                    :data-cy="`guidedRecording-invitations-detail-error-${invitation.id}`"
                  >
                    <div class="d-flex flex-wrap align-center justify-space-between ga-3">
                      <span>{{ detailError }}</span>
                      <v-btn variant="outlined" @click="loadInvitation(invitation.id)">
                        {{ $t('routes.guidedRecording.retryInvitationDetails') }}
                      </v-btn>
                    </div>
                  </v-alert>

                  <article
                    v-else-if="selectedInvitation"
                    :aria-labelledby="`guided-recording-invitation-${invitation.id}-title`"
                    :data-cy="`guidedRecording-invitations-detail-${invitation.id}`"
                  >
                    <header class="d-flex flex-wrap align-center justify-space-between ga-3 mb-5">
                      <h2 :id="`guided-recording-invitation-${invitation.id}-title`" class="text-h6" tabindex="-1">
                        {{ selectedInvitation.title }}
                      </h2>
                      <v-chip
                        v-for="status in statuses(selectedInvitation)"
                        :key="status"
                        :color="status === 'active' ? 'success' : 'secondary'"
                        variant="tonal"
                      >
                        {{ $t(`routes.guidedRecording.${status}`) }}
                      </v-chip>
                    </header>

                    <section
                      class="d-flex flex-wrap align-center ga-2 mb-5"
                      :aria-label="$t('routes.guidedRecording.invitationActions')"
                      :data-cy="`guidedRecording-invitations-actions-${invitation.id}`"
                    >
                      <v-btn
                        color="primary"
                        variant="outlined"
                        :aria-label="$t('routes.guidedRecording.editInvitation')"
                        :data-cy="`guidedRecording-invitations-edit-${invitation.id}`"
                        @click="openOrganizer(selectedInvitation)"
                      >
                        <v-icon start aria-hidden="true">mdi-pencil-outline</v-icon>
                        {{ $t('common.edit') }}
                      </v-btn>
                      <v-btn
                        v-if="canDeleteInvitation(selectedInvitation)"
                        color="error"
                        variant="outlined"
                        :aria-label="$t('routes.guidedRecording.deleteInvitation')"
                        :data-cy="`guidedRecording-invitations-delete-${invitation.id}`"
                        @click="openDeleteDialog(selectedInvitation, $event)"
                      >
                        <v-icon start aria-hidden="true">mdi-delete-outline</v-icon>
                        {{ $t('common.delete') }}
                      </v-btn>
                    </section>

                    <section
                      v-if="hasPublicUrl(selectedInvitation)"
                      class="guided-recording-invitations-dialog__public-url mb-5"
                      :aria-labelledby="`guided-recording-invitation-${invitation.id}-public-url-heading`"
                    >
                      <h3 :id="`guided-recording-invitation-${invitation.id}-public-url-heading`" class="text-subtitle-1 mb-2">
                        {{ $t('routes.guidedRecording.publicUrl') }}
                      </h3>
                      <div class="guided-recording-invitations-dialog__public-url-value">
                        <code :data-cy="`guidedRecording-invitations-publicUrl-${invitation.id}`">
                          {{ publicInvitationUrl(selectedInvitation) }}
                        </code>
                        <v-btn
                          icon="mdi-content-copy"
                          variant="text"
                          :aria-label="$t('routes.guidedRecording.copyPublicUrl')"
                          :data-cy="`guidedRecording-invitations-copyPublicUrl-${invitation.id}`"
                          @click="copyPublicInvitationUrl(selectedInvitation)"
                        />
                      </div>
                    </section>

                    <v-row>
                      <v-col cols="12" md="7">
                        <section :aria-labelledby="`guided-recording-invitation-${invitation.id}-configuration-heading`">
                          <h3 :id="`guided-recording-invitation-${invitation.id}-configuration-heading`" class="text-subtitle-1 mb-3">
                            {{ $t('routes.guidedRecording.invitationConfiguration') }}
                          </h3>
                          <dl class="guided-recording-invitations-dialog__definition-list">
                            <div>
                              <dt>{{ $t('routes.guidedRecording.destination') }}</dt>
                              <dd>
                                <router-link
                                  v-if="nodeId(selectedInvitation.destinationnode)"
                                  :to="`/node/${nodeId(selectedInvitation.destinationnode)}`"
                                  :data-cy="`guidedRecording-invitations-detailDestination-${invitation.id}`"
                                  @click="dialog = false"
                                >
                                  {{ nodeName(selectedInvitation.destinationnode) }}
                                </router-link>
                                <span v-else>{{ nodeName(selectedInvitation.destinationnode) }}</span>
                              </dd>
                            </div>
                            <div>
                              <dt>{{ $t('routes.guidedRecording.organizerConsentRequired') }}</dt>
                              <dd>{{ booleanLabel(selectedInvitation.consentrequired) }}</dd>
                            </div>
                            <div>
                              <dt>{{ $t('routes.guidedRecording.organizerAudioAnalysisEnabled') }}</dt>
                              <dd>{{ booleanLabel(selectedInvitation.audioanalysisenabled) }}</dd>
                            </div>
                          </dl>
                        </section>

                      </v-col>

                      <v-col cols="12" md="5">
                        <section :aria-labelledby="`guided-recording-invitation-${invitation.id}-lifecycle-heading`">
                          <h3 :id="`guided-recording-invitation-${invitation.id}-lifecycle-heading`" class="text-subtitle-1 mb-3">
                            {{ $t('routes.guidedRecording.invitationLifecycle') }}
                          </h3>
                          <dl class="guided-recording-invitations-dialog__definition-list">
                            <div>
                              <dt>{{ $t('routes.guidedRecording.createdAt') }}</dt>
                              <dd>{{ formatDate(selectedInvitation.createdat) }}</dd>
                            </div>
                            <div>
                              <dt>{{ $t('routes.guidedRecording.updatedAt') }}</dt>
                              <dd>{{ formatDate(selectedInvitation.updatedat) }}</dd>
                            </div>
                            <div v-if="expiryDate(selectedInvitation)">
                              <dt>{{ $t('routes.guidedRecording.expiryDate') }}</dt>
                              <dd>{{ formatDate(expiryDate(selectedInvitation)) }}</dd>
                            </div>
                          </dl>
                        </section>
                      </v-col>
                      <v-col
                        v-if="selectedInvitation.introduction || selectedInvitation.instructions"
                        cols="12"
                      >
                        <section :aria-labelledby="`guided-recording-invitation-${invitation.id}-details-heading`">
                          <h3 :id="`guided-recording-invitation-${invitation.id}-details-heading`" class="text-subtitle-1 mb-3">
                            {{ $t('routes.guidedRecording.invitationDetails') }}
                          </h3>
                          <dl class="guided-recording-invitations-dialog__definition-list">
                            <div v-if="selectedInvitation.introduction">
                              <dt>{{ $t('routes.guidedRecording.introduction') }}</dt>
                              <dd class="guided-recording-invitations-dialog__detail-text">
                                {{ selectedInvitation.introduction }}
                              </dd>
                            </div>
                            <div v-if="selectedInvitation.instructions">
                              <dt>{{ $t('routes.guidedRecording.instructions') }}</dt>
                              <dd class="guided-recording-invitations-dialog__detail-text">
                                {{ selectedInvitation.instructions }}
                              </dd>
                            </div>
                          </dl>
                        </section>
                      </v-col>
                    </v-row>

                  </article>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>

          <template v-slot:footer="{ page, items, pageCount, prevPage, nextPage }">
            <div v-if="items.length" class="d-flex flex-wrap align-center justify-end ga-3 mt-4">
              <span id="guided-recording-invitations-items-per-page">
                {{ $t('common.itemsPerPage') }}
              </span>
              <v-select
                v-model="itemsPerPage"
                aria-labelledby="guided-recording-invitations-items-per-page"
                density="compact"
                style="max-width: 76px"
                variant="underlined"
                :items="[10, 20, 30, 50]"
                hide-details
              />
              <span>
                {{ $t('common.fromToOutOf', {
                  from: Math.max(1, (page - 1) * itemsPerPage + 1),
                  to: Math.min(invitations.length, page * itemsPerPage),
                  total: invitations.length,
                }) }}
              </span>
              <v-btn
                icon="mdi-chevron-left"
                variant="text"
                :aria-label="$t('common.previousPage')"
                :disabled="page === 1"
                @click="prevPage"
              />
              <v-btn
                icon="mdi-chevron-right"
                variant="text"
                :aria-label="$t('common.nextPage')"
                :disabled="page === pageCount"
                @click="nextPage"
              />
            </div>
          </template>
        </v-data-iterator>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" data-cy="guidedRecording-invitations-close-action" @click="dialog = false">
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="deleteDialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    aria-labelledby="guided-recording-delete-dialog-title"
    aria-describedby="guided-recording-delete-dialog-description"
    class="vb-dialog vb-dialog-danger"
    data-cy="guidedRecording-invitations-deleteDialog"
  >
    <v-card>
      <v-card-title id="guided-recording-delete-dialog-title">
        {{ $t('routes.guidedRecording.deleteInvitationTitle') }}
      </v-card-title>
      <v-card-text class="pt-4">
        <p id="guided-recording-delete-dialog-description">{{ $t('routes.guidedRecording.deleteInvitationDescription') }}</p>
        <v-alert v-if="deleteError" ref="deleteError" type="error" variant="tonal" role="alert" tabindex="-1">
          {{ deleteError }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="error"
          :loading="isDeleting"
          :disabled="isDeleting"
          data-cy="guidedRecording-invitations-confirmDelete"
          @click="deleteInvitation"
        >
          {{ $t('routes.guidedRecording.deleteInvitationConfirm') }}
        </v-btn>
        <v-btn
          ref="cancelDeleteButton"
          variant="text"
          :disabled="isDeleting"
          data-cy="guidedRecording-invitations-cancelDelete"
          @click="cancelDelete"
        >
          {{ $t('common.cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

</template>

<script>
import Api from '@/api/Api.js';
import { isoToDatetime } from '@/components/formatters.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';

const toSafeInvitation = (invitation) => ({
  id: invitation.id,
  code: invitation.code,
  title: invitation.title,
  active: invitation.active === true,
  projectnode: invitation.projectnode ?? invitation.projectNode,
  destinationnode: invitation.destinationnode ?? invitation.destinationNode,
  introduction: invitation.introduction,
  instructions: invitation.instructions,
  consentrequired: invitation.consentrequired ?? invitation.consentRequired,
  consentstatementversion: invitation.consentstatementversion ?? invitation.consentStatementVersion,
  audioanalysisenabled: invitation.audioanalysisenabled ?? invitation.audioAnalysisEnabled,
  expiresat: invitation.expiresat ?? invitation.expiresAt ?? null,
  createdat: invitation.createdat ?? invitation.createdAt,
  updatedat: invitation.updatedat ?? invitation.updatedAt,
  questions: invitation.questions ?? [],
});

export default {
  name: 'GuidedRecordingInvitationsListDialog',
  emits: ['create', 'edit'],
  mixins: [DialogActionKeys, DialogModel],
  props: {
    node: { type: Object, required: true },
  },
  data() {
    return {
      invitations: [],
      selectedInvitation: null,
      selectedInvitationId: null,
      itemsPerPage: 10,
      isLoading: false,
      loadingInvitationId: null,
      listError: '',
      detailError: '',
      deleteDialog: false,
      deleteTarget: null,
      deleteTrigger: null,
      deleteError: '',
      isDeleting: false,
    };
  },
  watch: {
    dialog(isOpen) {
      if (isOpen) {
        this.loadInvitations();
      } else {
        this.clearSelection();
      }
    },
    listError(error) {
      if (error) {
        this.$nextTick(() => this.$refs.listError?.$el?.focus());
      }
    },
    deleteError(error) {
      if (error) {
        this.$nextTick(() => this.$refs.deleteError?.$el?.focus());
      }
    },
  },
  methods: {
    isoToDatetime,
    async loadInvitations() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;
      this.listError = '';
      this.clearSelection();
      try {
        const invitations = await Api.guidedRecording.getInvitations(this.node.id);
        this.invitations = Array.isArray(invitations) ? invitations.map(toSafeInvitation) : [];
      } catch (error) {
        this.invitations = [];
        this.listError = this.$localizeMessage(
          error?.response?.data?.error,
          this.$t('routes.guidedRecording.invitationsLoadFailed'),
        );
      } finally {
        this.isLoading = false;
      }
    },
    async loadInvitation(invitationId) {
      if (!invitationId || this.loadingInvitationId === invitationId) {
        return;
      }

      this.selectedInvitation = null;
      this.detailError = '';
      this.loadingInvitationId = invitationId;
      try {
        const invitation = await Api.guidedRecording.getInvitation(this.node.id, invitationId);
        if (this.selectedInvitationId === invitationId) {
          this.selectedInvitation = toSafeInvitation(invitation);
        }
      } catch (error) {
        if (this.selectedInvitationId === invitationId) {
          this.detailError = this.$localizeMessage(
            error?.response?.data?.error,
            this.$t('routes.guidedRecording.invitationDetailsLoadFailed'),
          );
        }
      } finally {
        if (this.loadingInvitationId === invitationId) {
          this.loadingInvitationId = null;
        }
      }
    },
    clearSelection() {
      this.selectedInvitation = null;
      this.selectedInvitationId = null;
      this.loadingInvitationId = null;
      this.detailError = '';
    },
    invitationProjectNodeId(invitation) {
      return typeof invitation?.projectnode?.id === 'string' ? invitation.projectnode.id : '';
    },
    nodeId(node) {
      return typeof node?.id === 'string' && node.id ? node.id : '';
    },
    openOrganizer(invitation) {
      if (!this.invitationProjectNodeId(invitation)) {
        return;
      }
      this.$emit('edit', invitation);
    },
    openDeleteDialog(invitation, event) {
      if (!this.canDeleteInvitation(invitation) || !this.invitationProjectNodeId(invitation)) {
        return;
      }

      this.deleteTarget = invitation;
      this.deleteTrigger = event?.currentTarget || null;
      this.deleteError = '';
      this.deleteDialog = true;
      this.$nextTick(() => this.$refs.cancelDeleteButton?.$el?.focus());
    },
    cancelDelete() {
      if (this.isDeleting) {
        return;
      }

      this.deleteDialog = false;
      this.deleteTarget = null;
      this.deleteError = '';
      this.$nextTick(() => this.deleteTrigger?.focus());
    },
    async deleteInvitation() {
      const invitation = this.deleteTarget;
      const projectNodeId = this.invitationProjectNodeId(invitation);
      if (this.isDeleting || !this.canDeleteInvitation(invitation) || !projectNodeId) {
        return;
      }

      this.deleteError = '';
      this.isDeleting = true;
      try {
        await Api.guidedRecording.deleteInvitation(projectNodeId, invitation.id);
        this.invitations = this.invitations.filter(({ id }) => id !== invitation.id);
        if (this.selectedInvitationId === invitation.id) {
          this.clearSelection();
        }
        this.deleteDialog = false;
        this.deleteTarget = null;
        this.$showMessage(this.$t('routes.guidedRecording.deleteInvitationSuccess'));
        this.$nextTick(() => document.getElementById('guided-recording-invitations-dialog-title')?.focus());
      } catch (error) {
        this.deleteError = this.$localizeMessage(
          error?.response?.data?.error,
          this.$t('routes.guidedRecording.deleteInvitationFailed'),
        );
      } finally {
        this.isDeleting = false;
      }
    },
    canDeleteInvitation(invitation) {
      if (invitation?.active === false) {
        return true;
      }
      const expiresAt = invitation?.expiresat ? new Date(invitation.expiresat).getTime() : NaN;
      return invitation?.active === true && Number.isFinite(expiresAt)
        && Date.now() >= expiresAt + (30 * 60 * 1000);
    },
    closeDialogOnEscape() {
      if (this.deleteDialog) {
        this.cancelDelete();
        return true;
      }

      return false;
    },
    isDetailLoading(invitationId) {
      return this.loadingInvitationId === invitationId;
    },
    hasPublicUrl(invitation) {
      return typeof invitation?.code === 'string' && invitation.code.length > 0;
    },
    publicInvitationUrl(invitation) {
      if (!this.hasPublicUrl(invitation)) {
        return '';
      }

      return `${window.location.origin}/guided-recording/${invitation.code}`;
    },
    async copyPublicInvitationUrl(invitation) {
      const publicUrl = this.publicInvitationUrl(invitation);
      if (!publicUrl || !navigator.clipboard) {
        this.$showMessage(this.$t('routes.guidedRecording.copyFailed'), { type: 'error' });
        return;
      }

      try {
        await navigator.clipboard.writeText(publicUrl);
        this.$showMessage(this.$t('routes.guidedRecording.copiedPublicUrl'));
      } catch {
        this.$showMessage(this.$t('routes.guidedRecording.copyFailed'), { type: 'error' });
      }
    },
    nodeName(node) {
      return node?.name || this.$t('routes.guidedRecording.nodeUnavailable');
    },
    formatDate(value) {
      return value ? this.isoToDatetime(value) : this.$t('routes.guidedRecording.notAvailable');
    },
    expiryDate(invitation) {
      return invitation.expiresat;
    },
    statuses(invitation) {
      const statuses = [];
      if (invitation.active === false) {
        statuses.push('disabled');
      }
      const expiry = invitation.expiresat ? new Date(invitation.expiresat).getTime() : NaN;
      if (Number.isFinite(expiry) && expiry <= Date.now()) {
        statuses.push('expired');
      }
      return statuses.length ? statuses : ['active'];
    },
    booleanLabel(value) {
      return value === true
        ? this.$t('routes.guidedRecording.enabled')
        : this.$t('routes.guidedRecording.disabled');
    },
  },
};
</script>

<style lang="scss" scoped>
@layer app {
  .guided-recording-invitations-dialog__content {
    min-width: 0;
    width: 100%;
  }

  .guided-recording-invitations-dialog__title {
    font: inherit;
  }

  .guided-recording-invitations-dialog__panels {
    border-radius: 8px;
    overflow: hidden;
  }

  .guided-recording-invitations-dialog__panel-title {
    min-width: 0;
  }

  .guided-recording-invitations-dialog__panel-grid {
    display: grid;
    gap: 16px;
    grid-template-columns: minmax(0, 1fr);
    width: 100%;
  }

  .guided-recording-invitations-dialog__panel-primary,
  .guided-recording-invitations-dialog__panel-field {
    min-width: 0;
  }

  .guided-recording-invitations-dialog__public-url-value {
    align-items: center;
    border: 1px solid rgb(var(--v-theme-outline));
    border-radius: 4px;
    display: flex;
    gap: 8px;
    min-width: 0;
    padding: 4px 4px 4px 12px;
  }

  .guided-recording-invitations-dialog__public-url-value code {
    flex: 1 1 auto;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  @media (min-width: 600px) {
    .guided-recording-invitations-dialog__panel-grid {
      grid-template-columns: minmax(0, 5fr) minmax(0, 3fr);
    }
  }

  @media (min-width: 960px) {
    .guided-recording-invitations-dialog__panel-grid {
      grid-template-columns: minmax(0, 5fr) minmax(0, 3fr) minmax(0, 4fr);
    }
  }

  .guided-recording-invitations-dialog__definition-list {
    display: grid;
    gap: 12px;
    margin: 0;
  }

  .guided-recording-invitations-dialog__definition-list dt {
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    font-size: 0.875rem;
  }

  .guided-recording-invitations-dialog__definition-list dd {
    margin: 2px 0 0;
    overflow-wrap: anywhere;
  }

}

.guided-recording-invitations-dialog__detail-text {
  white-space: pre-wrap;
}
</style>
